import React from 'react';
import PropTypes from 'prop-types';
import {defineMessages, injectIntl, intlShape} from 'react-intl';

import styles from './auth-modal.css';

const messages = defineMessages({
    loginTitle: {
        id: 'gui.auth.loginTitle',
        defaultMessage: 'Sign In',
        description: 'Title for login modal'
    },
    registerTitle: {
        id: 'gui.auth.registerTitle',
        defaultMessage: 'Create Account',
        description: 'Title for register modal'
    },
    username: {
        id: 'gui.auth.username',
        defaultMessage: 'Username',
        description: 'Label for username field'
    },
    email: {
        id: 'gui.auth.email',
        defaultMessage: 'Email',
        description: 'Label for email field'
    },
    password: {
        id: 'gui.auth.password',
        defaultMessage: 'Password',
        description: 'Label for password field'
    },
    displayName: {
        id: 'gui.auth.displayName',
        defaultMessage: 'Display Name',
        description: 'Label for display name field'
    },
    signIn: {
        id: 'gui.auth.signIn',
        defaultMessage: 'Sign In',
        description: 'Sign in button text'
    },
    createAccount: {
        id: 'gui.auth.createAccount',
        defaultMessage: 'Create Account',
        description: 'Create account button text'
    },
    switchToRegister: {
        id: 'gui.auth.switchToRegister',
        defaultMessage: 'Need an account? Sign up',
        description: 'Link to switch to register form'
    },
    switchToLogin: {
        id: 'gui.auth.switchToLogin',
        defaultMessage: 'Already have an account? Sign in',
        description: 'Link to switch to login form'
    },
    pendingApprovalTitle: {
        id: 'gui.auth.pendingApprovalTitle',
        defaultMessage: 'Account Created',
        description: 'Title when account is pending approval'
    },
    pendingApprovalMessage: {
        id: 'gui.auth.pendingApprovalMessage',
        defaultMessage: 'Your account has been created and is awaiting admin approval. You will be able to sign in once your account is approved.',
        description: 'Message shown when account requires admin approval'
    },
    pendingApprovalOk: {
        id: 'gui.auth.pendingApprovalOk',
        defaultMessage: 'OK',
        description: 'Button to close pending approval message'
    }
});

const AuthModalComponent = ({
    intl,
    isLogin,
    isLoading,
    error,
    pendingApproval,
    gated,
    username,
    email,
    password,
    displayName,
    onUsernameChange,
    onEmailChange,
    onPasswordChange,
    onDisplayNameChange,
    onSubmit,
    onSwitchMode,
    onClose,
    onPendingBack
}) => (
    <div
        className={gated ? styles.gatedContainer : styles.modalOverlay}
        onClick={gated ? undefined : onClose}
    >
        <div
            className={styles.modal}
            onClick={e => e.stopPropagation()}
        >
            {pendingApproval ? (
                <div className={styles.pendingApproval}>
                    <h2 className={styles.title}>
                        {intl.formatMessage(messages.pendingApprovalTitle)}
                    </h2>
                    <p className={styles.pendingMessage}>
                        {intl.formatMessage(messages.pendingApprovalMessage)}
                    </p>
                    <button
                        className={styles.submitButton}
                        onClick={gated ? (onPendingBack || onSwitchMode) : onClose}
                    >
                        {intl.formatMessage(gated ? messages.switchToLogin : messages.pendingApprovalOk)}
                    </button>
                </div>
            ) : (
                <React.Fragment>
                    <h2 className={styles.title}>
                        {intl.formatMessage(isLogin ? messages.loginTitle : messages.registerTitle)}
                    </h2>

                    {error && (
                        <p className={styles.error}>{error}</p>
                    )}

                    <form
                        className={styles.form}
                        onSubmit={onSubmit}
                    >
                        <div className={styles.field}>
                            <label className={styles.label}>
                                {intl.formatMessage(messages.username)}
                            </label>
                            <input
                                autoFocus
                                className={styles.input}
                                type="text"
                                value={username}
                                onChange={onUsernameChange}
                            />
                        </div>

                        {!isLogin && (
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {intl.formatMessage(messages.email)}
                                </label>
                                <input
                                    className={styles.input}
                                    type="email"
                                    value={email}
                                    onChange={onEmailChange}
                                />
                            </div>
                        )}

                        {!isLogin && (
                            <div className={styles.field}>
                                <label className={styles.label}>
                                    {intl.formatMessage(messages.displayName)}
                                </label>
                                <input
                                    className={styles.input}
                                    type="text"
                                    value={displayName}
                                    onChange={onDisplayNameChange}
                                />
                            </div>
                        )}

                        <div className={styles.field}>
                            <label className={styles.label}>
                                {intl.formatMessage(messages.password)}
                            </label>
                            <input
                                className={styles.input}
                                type="password"
                                value={password}
                                onChange={onPasswordChange}
                            />
                        </div>

                        <button
                            className={styles.submitButton}
                            disabled={isLoading}
                            type="submit"
                        >
                            {intl.formatMessage(isLogin ? messages.signIn : messages.createAccount)}
                        </button>
                    </form>

                    <div className={styles.switchText}>
                        <button
                            className={styles.switchLink}
                            onClick={onSwitchMode}
                        >
                            {intl.formatMessage(isLogin ? messages.switchToRegister : messages.switchToLogin)}
                        </button>
                    </div>
                </React.Fragment>
            )}
        </div>
    </div>
);

AuthModalComponent.propTypes = {
    displayName: PropTypes.string,
    email: PropTypes.string,
    error: PropTypes.string,
    gated: PropTypes.bool,
    intl: intlShape,
    isLoading: PropTypes.bool,
    isLogin: PropTypes.bool,
    onClose: PropTypes.func,
    onDisplayNameChange: PropTypes.func,
    onEmailChange: PropTypes.func,
    onPasswordChange: PropTypes.func,
    onPendingBack: PropTypes.func,
    onSubmit: PropTypes.func,
    onSwitchMode: PropTypes.func,
    onUsernameChange: PropTypes.func,
    password: PropTypes.string,
    pendingApproval: PropTypes.bool,
    username: PropTypes.string
};

export default injectIntl(AuthModalComponent);
